/**
 * Crowd intensity from the game state (0..1, pure and allocation-free; called every rendered frame).
 *
 * The stadium is a character: it murmurs between plays, leans in on 3rd/4th down and in the red zone, rises as
 * the ball nears the end zone, holds its breath while a pass hangs in the air (more for a Hail Mary), and gets
 * loud for a breakaway. One-shot reactions (cheer/roar/boo…) are fired from events, not from here.
 */
import { CAMERA, MatchFormat, RULES, yardsToGoal } from '@hf/shared';
import { BallState, Phase, type SimState } from '@hf/sim';

export type CrowdMode = 'match' | 'practice' | 'tutorial';

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

/** Calm level between plays (the stadium is never silent during a match). */
export const CROWD_FLOOR = 0.2;

/** Points within which a game counts as close (one score). */
const CLOSE_GAME = 8;

/**
 * Late-game pressure: the crowd is on its feet when the result is still in doubt — Classic: the last 30 s of the
 * second half; Drives: both players on their final possession — and the score is within one score.
 */
export const clutch = (s: SimState): number => {
  const r = s.rules;
  if (r.overtime || r.phase === Phase.GAME_OVER) return 0;
  const diff = Math.abs((r.score[0] ?? 0) - (r.score[1] ?? 0));
  if (diff > CLOSE_GAME) return 0;
  if (r.format === MatchFormat.CLASSIC)
    return r.half >= 2 && r.gameClock <= RULES.classicClockStopsLastFrames ? 0.1 : 0;
  const last = r.drivesPerPlayer;
  return last > 0 && (r.drivesStarted[0] ?? 0) >= last && (r.drivesStarted[1] ?? 0) >= last ? 0.08 : 0;
};

export const crowdLevel = (s: SimState, mode: CrowdMode = 'match'): number => {
  const r = s.rules;
  const ball = s.ball;
  const dir = r.attackDir || 1;
  let level: number;
  switch (r.phase) {
    case Phase.PRE_SNAP: {
      level = 0.28;
      if (r.down === 3) level += 0.08;
      else if (r.down >= 4) level += 0.16;
      const ytg = yardsToGoal(r.los, dir);
      if (ytg < 20) level += ((20 - Math.max(0, ytg)) / 20) * 0.15;
      break;
    }
    case Phase.RUSH_COUNT:
    case Phase.LIVE: {
      level = 0.38;
      const ytg = yardsToGoal(ball.x, dir);
      level += clamp01(1 - ytg / 50) * 0.32;
      if (ball.state === BallState.AIR) level += ball.hailMary ? 0.28 : 0.14;
      else if (ball.state === BallState.HELD && ball.holder >= 0) {
        // breakaway: the carrier is past the defender, heading for the goal at speed
        const carrier = s.players[ball.holder as 0 | 1];
        const defender = s.players[(1 - ball.holder) as 0 | 1];
        const carrierDir = ball.holder === r.offense ? dir : -dir;
        const lead = (carrier.x - defender.x) * carrierDir;
        const speed = carrier.vx * carrierDir;
        if (lead > 3 && speed > 4) level += Math.min(0.18, (lead - 3) * 0.03 + 0.06);
      }
      break;
    }
    case Phase.DEAD:
      level = 0.42;
      break;
    case Phase.CONVERSION:
      level = 0.55;
      break;
    case Phase.GAME_OVER:
      level = 0.6;
      break;
    default:
      level = 0.3;
  }
  if (r.overtime) level += 0.08;
  level += clutch(s);
  level = clamp01(Math.max(CROWD_FLOOR, level));
  return mode === 'match' ? level : CROWD_FLOOR + (level - CROWD_FLOOR) * 0.4;
};

/**
 * Stereo pan for a world x: clamp((x − cameraX) / (CAMERA.viewWidth / 2), −1, 1), softened so nothing sits
 * hard in one ear (README contract).
 */
export const panFor = (x: number, cameraX: number): number => {
  if (!Number.isFinite(x) || !Number.isFinite(cameraX)) return 0;
  const p = (x - cameraX) / (CAMERA.viewWidth / 2);
  return (p < -1 ? -1 : p > 1 ? 1 : p) * 0.85;
};
